import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button, Preloader, Toggle } from '@smartshell/ui-kit';
import { Editor as ReactEditor } from '@tinymce/tinymce-react';
import { Editor as TinyEditor } from 'tinymce';

import { SettingsLock } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';
import { ETheme, ThemeContext } from 'theme';

import s from './ClubRules.module.scss';

export const ClubRules: FC = () => {
  const { t } = useContext(LocalizationContext);
  const [tinyKey, setTinyKey] = useState(0);
  const editorRef = useRef<TinyEditor | null>(null);
  const {
    state: {
      settings: { clubRules, isClubRulesEnabled },
      isLoading,
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const { theme } = useContext(ThemeContext);
  const isAvailable = useIsFeatureAvailable('club_rules');

  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    setTinyKey((prev) => prev + 1);
  }, [t, theme]);

  const themeProps = useMemo(() => {
    return theme === ETheme.DARK
      ? {
          skin: 'oxide-dark',
          content_css: 'dark',
          content_style: `.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
    color: rgba(255, 255, 255, 0.5);
    opacity: 1;
    }
    `,
        }
      : {};
  }, [theme]);

  return (
    <div className={s.container}>
      <div className={s.frame}>
        <Toggle
          onChange={(value: boolean) =>
            setSetting({
              alias: ESettingsAliases.CLUB_RULES_ENABLED,
              value: value ? '1' : '0',
            })
          }
          value={isClubRulesEnabled}
          label={t('settings.shell.lang.rules.title')}
          description={t('settings.shell.lang.rules.description')}
          variant="secondary"
          emptyBg
          disabled={!isAvailable || isLoading}
          rightContent={!isAvailable ? <SettingsLock /> : undefined}
        />
        {isClubRulesEnabled && (
          <>
            <Preloader loading={isInitializing} />
            <ReactEditor
              key={tinyKey}
              onInit={(evt, editor) => {
                setIsInitializing(false);
                editorRef.current = editor;
              }}
              disabled={isLoading}
              initialValue={clubRules}
              init={{
                height: 314,
                menubar: false,
                resize: false,
                plugins: ['lists'],

                placeholder: t('setting.shell.rules.placeholder'),
                block_formats: t('setting.shell.rules.blockFormats'),
                toolbar: 'blocks | bold italic forecolor | bullist numlist',
                invalid_elements:
                  'script,iframe,object,embed,form,input,textarea',
                cleanup_on_startup: true,
                xss_sanitization: true,
                convert_urls: false,
                ...themeProps,
              }}
            />
          </>
        )}
      </div>
      {isClubRulesEnabled && (
        <Button
          onClick={() => {
            const content = editorRef.current?.getContent();
            if (typeof content === 'undefined') {
              return;
            }

            if (content !== clubRules) {
              setSetting({
                alias: ESettingsAliases.CLUB_RULES_TEXT,
                value: content,
              });
            }
          }}
          disabled={isLoading}
          className={s['save']}
        >
          {t('save')}
        </Button>
      )}
    </div>
  );
};
